/**
 * The tools' variants and the wheel gestures (pure, tested in variants.test.ts; docs/features/sketch.md § 9a).
 *
 * - Variants: what a tool's flyout offers (hover or long-press a tool in the rail): obstacles (chimney, vent,
 *   skylight, box, cylinder), trees (deciduous, evergreen, conifer), panels (Magnetic or Free × portrait or
 *   landscape), dormers (gable, hip, shed), roof lines (eave … rake), buildings and walls. Picking one selects the
 *   tool with it.
 * - Wheel gestures (modifier + wheel over the map, editor/hotkeys.ts `sketch.wheel*`): on the selected obstacle or
 *   tree, else on the next one the tool places, else on the panels: size (or turn the selected panels), shape or kind
 *   (or portrait ⇄ landscape), height. `readout` is the small label the map shows while they change.
 */
import type { ObstacleItem } from "../editor/model.ts";
import type { Orientation } from "../editor/snap.ts";
import type { DormerKind } from "./dormers.ts";
import type { PlaceMode } from "./gizmo.ts";
import { centroid, MAX_HEIGHT, MAX_RADIUS, MIN_RADIUS, OBSTACLE_PRESETS, obstacleAt, obstacleCircle, shapeOf, treeAt, type Crown, type Leaf, type ObstaclePreset, type ObstacleShape, type ShadeItem } from "./shade.ts";
import type { EdgeType } from "./skeleton.ts";
import type { NeighbourKind, SketchTool } from "./types.ts";
import type { Vec2 } from "../editor/geo.ts";
import type { MessageKey } from "../i18n/keys";
import { metres, type Text } from "./text.ts";

/** The next obstacle, tree, building or wall the shade tools place. Sizes and heights null: the kind's default. */
export interface ShadeOptions {
  crown: Crown;
  leaf: Leaf;
  obstacle: ObstaclePreset;
  shape: ObstacleShape;
  neighbour: NeighbourKind;
  /** A box's side or a cylinder's diameter (m). */
  obstacleSize?: number | null;
  /** Above the roof (m). */
  obstacleHeight?: number | null;
  treeRadius?: number | null;
  treeHeight?: number | null;
  neighbourHeight?: number | null;
}

export const DEFAULT_SHADE_OPTIONS: ShadeOptions = { crown: "round", leaf: "deciduous", obstacle: "chimney", shape: "box", neighbour: "building" };

// ------------------------------------------------------------------------------------------------ variants
export interface ObstacleVariant { id: string; kind: ObstaclePreset; shape: ObstacleShape; label: MessageKey }
export const OBSTACLE_VARIANTS: ObstacleVariant[] = [
  { id: "chimney", kind: "chimney", shape: "box", label: "sketch.obstacle.chimney" },
  { id: "vent", kind: "vent", shape: "cylinder", label: "sketch.obstacle.vent" },
  { id: "skylight", kind: "skylight", shape: "box", label: "sketch.obstacle.skylight" },
  { id: "box", kind: "unknown", shape: "box", label: "sketch.obstacle.box" },
  { id: "cylinder", kind: "unknown", shape: "cylinder", label: "sketch.obstacle.cylinder" },
];

export interface TreeVariant { id: string; crown: Crown; leaf: Leaf; label: MessageKey; height: number }
export const TREE_VARIANTS: TreeVariant[] = [
  { id: "deciduous", crown: "round", leaf: "deciduous", label: "sketch.tree.deciduous", height: 10 },
  { id: "evergreen", crown: "round", leaf: "evergreen", label: "sketch.tree.evergreen", height: 10 },
  { id: "conifer", crown: "conical", leaf: "evergreen", label: "sketch.tree.conifer", height: 14 },
];

export interface PanelVariant { id: string; mode: PlaceMode; orientation: Orientation; label: MessageKey }
export const PANEL_VARIANTS: PanelVariant[] = [
  { id: "magnetic-portrait", mode: "magnetic", orientation: "portrait", label: "sketch.panelVariant.magneticPortrait" },
  { id: "magnetic-landscape", mode: "magnetic", orientation: "landscape", label: "sketch.panelVariant.magneticLandscape" },
  { id: "free-portrait", mode: "free", orientation: "portrait", label: "sketch.panelVariant.freePortrait" },
  { id: "free-landscape", mode: "free", orientation: "landscape", label: "sketch.panelVariant.freeLandscape" },
];

export const DORMER_VARIANTS: { id: DormerKind; label: MessageKey }[] = [
  { id: "gable", label: "sketch.dormer.gableDormer" },
  { id: "hip", label: "sketch.dormer.hipDormer" },
  { id: "shed", label: "sketch.dormer.shedDormer" },
];

export const LINE_VARIANTS: { id: EdgeType; label: MessageKey }[] = [
  { id: "eave", label: "sketch.line.eave" },
  { id: "ridge", label: "sketch.line.ridge" },
  { id: "hip", label: "sketch.line.hip" },
  { id: "valley", label: "sketch.line.valley" },
  { id: "rake", label: "sketch.line.rake" },
];

export const NEIGHBOUR_VARIANTS: { id: NeighbourKind; label: MessageKey }[] = [
  { id: "building", label: "sketch.neighbour.building" },
  { id: "wall", label: "sketch.neighbour.wall" },
];

/** What the tools are set to (the editor's state the flyouts read and change). */
export interface ToolState {
  tool: SketchTool;
  shade: ShadeOptions;
  placeMode: PlaceMode;
  orientation: Orientation;
  dormer: DormerKind;
  lineType: EdgeType;
}

/** A tool's flyout entries (id, label key) and which one is current; none for tools without variants. */
export function variantsOf(tool: SketchTool, s: Omit<ToolState, "tool">): { id: string; label: MessageKey; active: boolean }[] {
  switch (tool) {
    case "obstacle":
      return OBSTACLE_VARIANTS.map((v) => ({ id: v.id, label: v.label, active: obstacleVariantId(s.shade.obstacle, s.shade.shape) === v.id }));
    case "tree":
      return TREE_VARIANTS.map((v) => ({ id: v.id, label: v.label, active: treeVariantId(s.shade.crown, s.shade.leaf) === v.id }));
    case "panel":
      return PANEL_VARIANTS.map((v) => ({ id: v.id, label: v.label, active: v.mode === s.placeMode && v.orientation === s.orientation }));
    case "dormer":
      return DORMER_VARIANTS.map((v) => ({ id: v.id, label: v.label, active: v.id === s.dormer }));
    case "line":
      return LINE_VARIANTS.map((v) => ({ id: v.id, label: v.label, active: v.id === s.lineType }));
    case "neighbour":
      return NEIGHBOUR_VARIANTS.map((v) => ({ id: v.id, label: v.label, active: v.id === s.shade.neighbour }));
    default:
      return [];
  }
}

/** The tool state after picking a flyout entry: the tool is selected with that variant. */
export function pickVariant(s: ToolState, tool: SketchTool, id: string): ToolState {
  const next = { ...s, tool };
  if (tool === "obstacle") {
    const v = OBSTACLE_VARIANTS.find((x) => x.id === id);
    return v ? { ...next, shade: { ...s.shade, obstacle: v.kind, shape: v.shape, obstacleSize: null, obstacleHeight: null } } : next;
  }
  if (tool === "tree") {
    const v = TREE_VARIANTS.find((x) => x.id === id);
    return v ? { ...next, shade: { ...s.shade, crown: v.crown, leaf: v.leaf, treeHeight: null } } : next;
  }
  if (tool === "panel") {
    const v = PANEL_VARIANTS.find((x) => x.id === id);
    return v ? { ...next, placeMode: v.mode, orientation: v.orientation } : next;
  }
  if (tool === "dormer" && DORMER_VARIANTS.some((x) => x.id === id)) return { ...next, dormer: id as DormerKind };
  if (tool === "line" && LINE_VARIANTS.some((x) => x.id === id)) return { ...next, lineType: id as EdgeType };
  if (tool === "neighbour" && NEIGHBOUR_VARIANTS.some((x) => x.id === id)) return { ...next, shade: { ...s.shade, neighbour: id as NeighbourKind } };
  return next;
}

export function obstacleVariantId(kind: string, shape: ObstacleShape): string {
  return OBSTACLE_VARIANTS.find((v) => v.kind === kind && v.shape === shape)?.id ?? OBSTACLE_VARIANTS.find((v) => v.kind === kind)?.id ?? (shape === "cylinder" ? "cylinder" : "box");
}

export function treeVariantId(crown: Crown, leaf: Leaf): string {
  return crown === "conical" ? "conifer" : leaf === "evergreen" ? "evergreen" : "deciduous";
}

// ------------------------------------------------------------------------------------------------ the next one
/** The obstacle a click at `at` places (the options' kind, shape, size and height). */
export function nextObstacle(o: ShadeOptions, at: Vec2, angleDeg = 0): ObstacleItem {
  const made = obstacleAt(o.obstacle, at, o.shape, angleDeg, o.obstacleSize ?? OBSTACLE_PRESETS[o.obstacle].size);
  return o.obstacleHeight != null ? { ...made, height_m: o.obstacleHeight } : made;
}

export const treeDefaultHeight = (crown: Crown) => (crown === "conical" ? 14 : 10);

/** The tree a click (and a crown dragged out to `radius`) places. */
export function nextTree(o: ShadeOptions, at: Vec2, radius?: number): ShadeItem {
  return treeAt(at, radius ?? o.treeRadius ?? undefined, { crown: o.crown, leaf: o.leaf, height: o.treeHeight ?? treeDefaultHeight(o.crown) });
}

// ------------------------------------------------------------------------------------------------ wheel steps
export type WheelGesture = "size" | "kind" | "height";
export const WHEEL_GESTURE: Record<string, WheelGesture> = { "sketch.wheelSize": "size", "sketch.wheelKind": "kind", "sketch.wheelHeight": "height" };

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));
const r2 = (v: number) => Math.round(v * 100) / 100;
export const OBSTACLE_MIN_M = 0.1;
export const OBSTACLE_MAX_M = 6;
const SIZE_FACTOR = 1.1;

/** A box's sides or a cylinder's diameter. */
export function obstacleDims(o: ObstacleItem): [number, number] {
  if (shapeOf(o) === "cylinder") {
    const d = 2 * obstacleCircle(o).radius;
    return [d, d];
  }
  const [a, b, , d] = o.polygon as [Vec2, Vec2, Vec2, Vec2];
  return [Math.hypot(b[0] - a[0], b[1] - a[1]), Math.hypot(d[0] - a[0], d[1] - a[1])];
}

/** The obstacle with `steps` wheel steps of a gesture: size ×1.1 a step (about its centre), the next or previous
 * variant (centre and size kept), height ±10 cm. */
export function wheelObstacle(o: ObstacleItem, gesture: WheelGesture, steps: number): ObstacleItem {
  if (!steps) return o;
  if (gesture === "height") return { ...o, height_m: clamp(r2((o.height_m ?? OBSTACLE_PRESETS[(o.kind as ObstaclePreset)]?.height ?? 0.5) + 0.1 * steps), 0, 10) };
  const c = centroid(o.polygon);
  if (gesture === "size") {
    const [w, d] = obstacleDims(o);
    const k = clamp(Math.pow(SIZE_FACTOR, steps), OBSTACLE_MIN_M / Math.min(w, d), OBSTACLE_MAX_M / Math.max(w, d));
    return { ...o, polygon: o.polygon.map(([x, y]) => [c[0] + (x - c[0]) * k, c[1] + (y - c[1]) * k] as Vec2) };
  }
  const ids = OBSTACLE_VARIANTS.map((v) => v.id);
  const at = ids.indexOf(obstacleVariantId(o.kind, shapeOf(o)));
  const v = OBSTACLE_VARIANTS[(((at + steps) % ids.length) + ids.length) % ids.length]!;
  const [w, d] = obstacleDims(o);
  const oldPreset = OBSTACLE_PRESETS[o.kind as ObstaclePreset];
  const height = oldPreset && o.height_m != null && Math.abs(o.height_m - oldPreset.height) > 1e-9 ? o.height_m : OBSTACLE_PRESETS[v.kind].height;
  let polygon = o.polygon;
  if (v.shape !== shapeOf(o)) {
    // box ↔ cylinder: the same footprint size (side = diameter), along the box's own direction
    const a = o.polygon[0]!, b = o.polygon[1]!;
    const angle = v.shape === "box" ? 0 : (Math.atan2(b[1] - a[1], b[0] - a[0]) * 180) / Math.PI;
    const size = Math.max(w, d);
    polygon = obstacleAt(v.kind, c, v.shape, angle, size).polygon;
  }
  return { ...o, kind: v.kind, shape: v.shape, polygon, height_m: height };
}

/** The tree with `steps` wheel steps: crown radius ±25 cm, the next type, height ±50 cm. */
export function wheelTree(t: ShadeItem, gesture: WheelGesture, steps: number): ShadeItem {
  if (!steps || t.kind !== "tree") return t;
  if (gesture === "size") return { ...t, radius: clamp(r2((t.radius ?? 3) + 0.25 * steps), MIN_RADIUS, MAX_RADIUS) };
  if (gesture === "height") {
    const height = clamp(r2(t.height + 0.5 * steps), 1, MAX_HEIGHT);
    return { ...t, height, crownBase: t.crownBase == null ? null : Math.min(t.crownBase, height - 0.5) };
  }
  const ids = TREE_VARIANTS.map((v) => v.id);
  const at = ids.indexOf(treeVariantId(t.crown, t.leaf));
  const v = TREE_VARIANTS[(((at + steps) % ids.length) + ids.length) % ids.length]!;
  const old = TREE_VARIANTS[at]!;
  const height = Math.abs(t.height - old.height) < 1e-9 ? v.height : t.height;
  return { ...t, crown: v.crown, leaf: v.leaf, height };
}

/** The next obstacle's or tree's options after `steps` wheel steps (the tool is placing, nothing is selected). */
export function wheelOptions(o: ShadeOptions, tool: "obstacle" | "tree", gesture: WheelGesture, steps: number): ShadeOptions {
  if (tool === "obstacle") {
    const sample = wheelObstacle(nextObstacle(o, [0, 0]), gesture, steps);
    const [w] = obstacleDims(sample);
    if (gesture === "kind") return { ...o, obstacle: sample.kind as ObstaclePreset, shape: shapeOf(sample), obstacleSize: o.obstacleSize == null ? null : r2(w), obstacleHeight: o.obstacleHeight == null ? null : sample.height_m };
    if (gesture === "size") return { ...o, obstacleSize: r2(w) };
    return { ...o, obstacleHeight: sample.height_m };
  }
  const sample = wheelTree(nextTree(o, [0, 0]), gesture, steps);
  if (gesture === "kind") return { ...o, crown: sample.crown, leaf: sample.leaf, treeHeight: o.treeHeight == null ? null : sample.height };
  if (gesture === "size") return { ...o, treeRadius: sample.radius ?? null };
  return { ...o, treeHeight: sample.height };
}

/** Wheel deltas in steps: a mouse notch (100 px, or 3 lines) is one step; a trackpad's small deltas add up. */
export function wheelSteps(acc: number, deltaX: number, deltaY: number, deltaMode = 0): { steps: number; acc: number } {
  // Shift + wheel scrolls sideways on macOS: the turn arrives as deltaX
  const raw = Math.abs(deltaY) >= Math.abs(deltaX) ? deltaY : deltaX;
  const px = deltaMode === 1 ? raw * 40 : deltaMode === 2 ? raw * 400 : raw;
  const total = acc + px;
  const STEP = 100;
  const steps = total > 0 ? Math.floor(total / STEP) : Math.ceil(total / STEP);
  // wheel down (positive) makes it smaller / lower / the previous kind: up grows it, as a zoom does
  return { steps: -steps, acc: total - steps * STEP };
}

// ------------------------------------------------------------------------------------------------ readout
/** The on-map label while a gesture changes something: "Chimney · 0.77 × 0.77 m · 1.00 m high". */
export function readout(tx: Text, item: { obstacle?: ObstacleItem; tree?: ShadeItem; panels?: { orientation: Orientation; turned?: number } }, gesture: WheelGesture): string {
  if (item.obstacle) {
    const o = item.obstacle;
    const label = tx.t(OBSTACLE_VARIANTS.find((v) => v.id === obstacleVariantId(o.kind, shapeOf(o)))!.label);
    const [w, d] = obstacleDims(o);
    const size = shapeOf(o) === "cylinder" ? tx.t("sketch.readout.diameter", { size: metres(tx, w) }) : tx.t("sketch.readout.box", { width: tx.num(w, 2), depth: tx.num(d, 2) });
    const height = metres(tx, o.height_m ?? 0);
    return gesture === "height" ? tx.t("sketch.readout.obstacleHeight", { label, height }) : gesture === "size" ? tx.t("sketch.readout.obstacleSize", { label, size }) : tx.t("sketch.readout.obstacleAll", { label, size, height });
  }
  if (item.tree) {
    const t = item.tree;
    const label = tx.t(TREE_VARIANTS.find((v) => v.id === treeVariantId(t.crown, t.leaf))!.label);
    return gesture === "size" ? tx.t("sketch.readout.treeCrown", { label, size: metres(tx, 2 * (t.radius ?? 3)) }) : tx.t("sketch.readout.treeHeight", { label, height: metres(tx, t.height) });
  }
  if (item.panels) {
    if (gesture === "kind") return tx.t(item.panels.orientation === "portrait" ? "sketch.orientation.portrait" : "sketch.orientation.landscape");
    const turned = item.panels.turned ?? 0;
    return tx.t("sketch.readout.turned", { deg: `${turned > 0 ? "+" : ""}${tx.num(turned, 0)}` });
  }
  return "";
}
